import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Car } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Discreet SEO internal-link cluster — designed for inventory/home page footers.
 * Renders two columns: 8 cities + top brands. Drives crawl into auto-generated landing pages.
 */
const SeoCityCluster = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ cities: [], makes: [] });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/seo/cities`).then((r) => r.json()).catch(() => ({ cities: [] })),
      fetch(`${API}/api/seo/makes`).then((r) => r.json()).catch(() => ({ makes: [] })),
    ]).then(([c, m]) => setData({ cities: c.cities || [], makes: (m.makes || []).slice(0, 12) }));
  }, []);

  if (!data.cities.length) return null;

  return (
    <section
      className="relative py-12 md:py-16 px-4 md:px-6 border-t border-slate-200/50"
      data-testid="seo-city-cluster"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {/* Cities */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-blue-600" />
              <h2 className="text-sm font-bold text-slate-600 uppercase tracking-[0.18em]">
                {t('notreProcessus.browseByCityTitle')}
              </h2>
            </div>
            <p className="text-slate-500 text-sm mb-5 max-w-md">
              {t('notreProcessus.browseByCitySubtitle')}
            </p>
            <div className="flex flex-wrap gap-2">
              {data.cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/voitures-usagees/${c.slug}`}
                  data-testid={`seo-city-link-${c.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm bg-white/60 backdrop-blur-sm hover:bg-white text-slate-700 hover:text-blue-700 border border-slate-200/70 hover:border-blue-300 px-3.5 py-1.5 rounded-full transition-all shadow-sm hover:shadow"
                >
                  <span className="text-[10px] text-blue-500">●</span>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Popular brands × Montreal */}
          {data.makes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Car className="h-4 w-4 text-blue-600" />
                <h2 className="text-sm font-bold text-slate-600 uppercase tracking-[0.18em]">
                  {t('notreProcessus.popularBrandsTitle')}
                </h2>
              </div>
              <p className="text-slate-500 text-sm mb-5 max-w-md">Montréal</p>
              <div className="flex flex-wrap gap-2">
                {data.makes.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/voitures-usagees/${m.slug}-montreal`}
                    data-testid={`seo-make-link-${m.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm bg-white/60 backdrop-blur-sm hover:bg-white text-slate-700 hover:text-blue-700 border border-slate-200/70 hover:border-blue-300 px-3.5 py-1.5 rounded-full transition-all shadow-sm hover:shadow"
                  >
                    {m.name}
                    <span className="text-[10px] text-slate-400 font-medium">{m.count}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SeoCityCluster;
